import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, Tooltip, FormControlLabel, Switch, Autocomplete } from '@mui/material';
import { ButtonBasic } from '../../components/button/ButtonBasic.jsx';
import { createOrUpdateCafe } from '../../services/cafeService';
import { getPublicAvatarUrl } from '../../utils/publicAvatarUtils';

/**
 * CafeAdminModal
 *
 * Props:
 * @param {boolean} opened - 모달 열림 여부 [Required]
 * @param {function} onClose - 닫기 핸들러 [Required]
 * @param {object} initialValue - 초기값(수정 모드) [Optional]
 *
 * Example usage:
 * <CafeAdminModal opened={open} onClose={()=>setOpen(false)} />
 */
export default function CafeAdminModal({ opened, onClose, initialValue = null, participants = [], collections = [] }) {
  const [id, setId] = useState(initialValue?.id || '');
  const [name, setName] = useState(initialValue?.name || '');
  const [lat, setLat] = useState(typeof initialValue?.lat === 'number' ? initialValue.lat : undefined);
  const [lng, setLng] = useState(typeof initialValue?.lng === 'number' ? initialValue.lng : undefined);
  const [description, setDescription] = useState(initialValue?.description || '');
  const [threadsUrl, setThreadsUrl] = useState(initialValue?.threadsUrl || '');
  const [cafeUrl, setCafeUrl] = useState(initialValue?.cafeUrl || '');
  const [address, setAddress] = useState(initialValue?.address || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [magazineParticipant, setMagazineParticipant] = useState(!!initialValue?.magazineParticipant);
  const [selectedUser, setSelectedUser] = useState(initialValue?.userInfo || null);
  const [collectionId, setCollectionId] = useState(initialValue?.collectionId || '');
  const [collectionPreview, setCollectionPreview] = useState(initialValue?.collectionPreview || undefined);

  const options = useMemo(() => {
    return (participants || []).map((u) => {
      const uid = String(u?.id || u?.user_id || u?.profile?.id || '');
      const name = u?.profile?.name || '';
      const username = u?.profile?.username || '';
      const label = username ? `${name} (@${username})` : name || uid;
      return { ...u, __uid: uid, __label: label };
    }).filter(o => !!o.__uid);
  }, [participants]);

  const collectionOptions = useMemo(() => {
    return (collections || []).map((c) => {
      const id = String(c?.id || '');
      const title = c?.title || c?.name || id;
      return { id, title, __label: title };
    }).filter(o => !!o.id);
  }, [collections]);

  const selectedUserOption = useMemo(() => {
    if (!selectedUser) return null;
    const uid = String(selectedUser?.id || selectedUser?.user_id || selectedUser?.profile?.id || '');
    return options.find((o) => o.__uid === uid) || null;
  }, [selectedUser, options]);

  const selectedCollectionOption = useMemo(() => {
    if (!collectionId) return null;
    return collectionOptions.find((o) => o.id === collectionId) || null;
  }, [collectionId, collectionOptions]);

  // 모달이 열릴 때마다 initialValue로 폼을 동기화(수정 시 프리필)
  useEffect(() => {
    if (!opened) return;
    setId(initialValue?.id || '');
    setName(initialValue?.name || '');
    setLat(typeof initialValue?.lat === 'number' ? initialValue.lat : undefined);
    setLng(typeof initialValue?.lng === 'number' ? initialValue.lng : undefined);
    setDescription(initialValue?.description || '');
    setThreadsUrl(initialValue?.threadsUrl || '');
    setCafeUrl(initialValue?.cafeUrl || '');
    setAddress(initialValue?.address || '');
    setMagazineParticipant(!!initialValue?.magazineParticipant);
    setSelectedUser(initialValue?.userInfo || null);
    setCollectionId(initialValue?.collectionId || '');
    setCollectionPreview(initialValue?.collectionPreview || undefined);
    setError('');
  }, [opened, initialValue]);

  const validate = () => {
    if (!name.trim()) return '카페 명은 필수입니다';
    // 좌표가 이미 채워져 있으면 범위 검사, 아니면 주소 지오코딩에 맡김
    if (lat != null) {
      if (typeof lat !== 'number' || lat < -90 || lat > 90) return '위도(lat) 범위를 확인하세요 (-90~90)';
    }
    if (lng != null) {
      if (typeof lng !== 'number' || lng < -180 || lng > 180) return '경도(lng) 범위를 확인하세요 (-180~180)';
    }
    if (magazineParticipant && !selectedUser) return '참여자 선택이 필요합니다';
    if (magazineParticipant && !collectionId) return '작성 컬렉션 선택이 필요합니다';
    return '';
  };

  const geocodeIfNeeded = async () => {
    // 좌표가 비어 있고 주소가 있으면 지오코딩 수행
    const hasCoords = typeof lat === 'number' && typeof lng === 'number';
    if (hasCoords || !address?.trim()) return { lat, lng };

    const token =
      (typeof window !== 'undefined' && (window.REACT_APP_MAPBOX_TOKEN || window.MAPBOX_TOKEN)) ||
      (typeof localStorage !== 'undefined' && localStorage.getItem('MAPBOX_TOKEN')) ||
      process.env.REACT_APP_MAPBOX_TOKEN || '';
    if (!token) {
      throw new Error('지오코딩 토큰이 없습니다 (MAPBOX_TOKEN)');
    }

    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
      address
    )}.json?country=KR&language=ko&limit=1&types=address,poi&access_token=${token}`;

    const res = await fetch(url);
    if (!res.ok) throw new Error('지오코딩 요청 실패');
    const json = await res.json();
    const feature = json?.features?.[0];
    if (!feature?.center || !Array.isArray(feature.center)) {
      throw new Error('주소로 좌표를 찾지 못했습니다');
    }
    const [resolvedLng, resolvedLat] = feature.center;
    return { lat: resolvedLat, lng: resolvedLng };
  };

  const handleSave = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setSaving(true);
    setError('');
    try {
      const { lat: finalLat, lng: finalLng } = await geocodeIfNeeded();
      if (typeof finalLat !== 'number' || typeof finalLng !== 'number') {
        throw new Error('좌표가 필요합니다 (주소 변환 실패)');
      }
      const buildUserInfo = (u) => {
        if (!u) return null;
        const uid = String(u?.id || u?.user_id || u?.profile?.id || '');
        const profile = {
          id: u?.profile?.id || uid || undefined,
          name: u?.profile?.name || undefined,
          username: u?.profile?.username || undefined,
        };
        // 항상 캐시 퍼블릭 아바타 URL을 사용
        const profileImage = uid ? getPublicAvatarUrl(uid, 160) : (u?.profileImage || u?.__fallbackAvatar || null);
        return { id: uid, profile, profileImage };
      };
      await createOrUpdateCafe({
        id: id?.trim() || undefined,
        name: name.trim(),
        lat: finalLat,
        lng: finalLng,
        description,
        threadsUrl,
        cafeUrl,
        address,
        magazineParticipant: !!magazineParticipant,
        userInfo: magazineParticipant ? buildUserInfo(selectedUser) : null,
        collectionId: magazineParticipant ? (collectionId || null) : null,
        collectionPreview,
      });
      // 저장 직후 폼 초기화
      try {
        setId('');
        setName('');
        setLat(undefined);
        setLng(undefined);
        setDescription('');
        setThreadsUrl('');
        setCafeUrl('');
        setAddress('');
        setCollectionId('');
        setCollectionPreview(undefined);
      } catch (_) {}
      onClose?.();
    } catch (e) {
      setError(e?.message || '저장 중 오류');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={opened} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>카페 등록/수정</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Tooltip title={id || ''} disableHoverListener={!id} placement="top-start">
            <TextField label="id (옵션)" placeholder="비우면 자동 생성" value={id} onChange={(e)=>setId(e.target.value)} fullWidth />
          </Tooltip>
          <Tooltip title={name || ''} disableHoverListener={!name} placement="top-start">
            <TextField label="카페 명" required value={name} onChange={(e)=>setName(e.target.value)} fullWidth />
          </Tooltip>
          <Tooltip title={address || ''} disableHoverListener={!address} placement="top-start">
            <TextField label="주소(선택, 좌표 없을 때 변환)" placeholder="예) 서울특별시 중구 세종대로 110" value={address} onChange={(e)=>setAddress(e.target.value)} fullWidth />
          </Tooltip>
          <Stack direction="row" spacing={2}>
            <TextField label="위도(lat)" required value={lat ?? ''} onChange={(e)=>setLat(e.target.value === '' ? undefined : Number(e.target.value))} fullWidth type="number" inputProps={{ step: 0.0001 }} />
            <TextField label="경도(lng)" required value={lng ?? ''} onChange={(e)=>setLng(e.target.value === '' ? undefined : Number(e.target.value))} fullWidth type="number" inputProps={{ step: 0.0001 }} />
          </Stack>
          <Tooltip title={description || ''} disableHoverListener={!description} placement="top-start">
            <TextField label="카페 설명" value={description} onChange={(e)=>setDescription(e.target.value)} fullWidth multiline minRows={3} />
          </Tooltip>
          <Tooltip title={threadsUrl || ''} disableHoverListener={!threadsUrl} placement="top-start">
            <TextField label="스레드 링크" value={threadsUrl} onChange={(e)=>setThreadsUrl(e.target.value)} fullWidth />
          </Tooltip>
          <Tooltip title={cafeUrl || ''} disableHoverListener={!cafeUrl} placement="top-start">
            <TextField label="카페 링크" value={cafeUrl} onChange={(e)=>setCafeUrl(e.target.value)} fullWidth />
          </Tooltip>
          <FormControlLabel
            control={<Switch checked={magazineParticipant} onChange={(e)=>setMagazineParticipant(e.target.checked)} />}
            label="매거진 참여자"
          />
          {magazineParticipant && (
            <Autocomplete
              options={options}
              getOptionLabel={(o)=>o.__label || ''}
              value={selectedUserOption}
              onChange={(_, v)=>setSelectedUser(v || null)}
              renderInput={(params)=>(<TextField {...params} label="참여자 선택" placeholder="이름 또는 @username 검색" />)}
              isOptionEqualToValue={(a,b)=>a?.__uid=== (b?.__uid || String(b?.id || b?.user_id || b?.profile?.id || ''))}
            />
          )}
          {magazineParticipant && (
            collectionOptions.length > 0 ? (
              <Autocomplete
                options={collectionOptions}
                getOptionLabel={(o)=>o.__label || ''}
                value={selectedCollectionOption}
                onChange={(_, v)=>{
                  setCollectionId(v?.id || '');
                  if (v) {
                    setCollectionPreview({ id: v.id, title: v.title });
                  } else {
                    setCollectionPreview(undefined);
                  }
                }}
                renderInput={(params)=>(<TextField {...params} label="작성 컬렉션 선택" placeholder="컬렉션 제목 검색" />)}
                isOptionEqualToValue={(a,b)=>a?.id=== (b?.id || '')}
              />
            ) : (
              <TextField label="컬렉션 ID" placeholder="예: collection_123" value={collectionId} onChange={(e)=>setCollectionId(e.target.value)} fullWidth />
            )
          )}
          {error ? <div style={{ color: 'red' }}>{error}</div> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <ButtonBasic variant="text" onClick={onClose}>취소</ButtonBasic>
        <ButtonBasic onClick={handleSave} disabled={saving} loading={saving}>
          저장
        </ButtonBasic>
      </DialogActions>
    </Dialog>
  );
}
